import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import {
  RefreshCw,
  ChevronUp,
  ChevronDown,
  Lock,
  Unlock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Activity,
  Shield,
  Wifi,
  WifiOff,
  ArrowUpDown,
  Zap,
  Link2,
  Clock,
  Server,
} from 'lucide-react';

const API_URL = '';

const directionConfig = {
  entry: { label: 'Entry', color: 'bg-blue-100 text-blue-800', icon: ChevronDown },
  exit: { label: 'Exit', color: 'bg-green-100 text-green-800', icon: ChevronUp },
  both: { label: 'Entry/Exit', color: 'bg-purple-100 text-purple-800', icon: ArrowUpDown },
};

const commandStatusConfig = {
  queued: { label: 'Queued', color: 'bg-yellow-100 text-yellow-800' },
  pending: { label: 'Pending', color: 'bg-blue-100 text-blue-800' },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-800' },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-800' },
};

function formatDateTime(isoString) {
  if (!isoString) return '-';
  const date = new Date(isoString);
  return date.toLocaleString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export default function AdminBarrierControl() {
  const [barriers, setBarriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bridgeHealth, setBridgeHealth] = useState({ is_online: false });
  const [statusInfo, setStatusInfo] = useState({ updated_at: null, is_stale: true });
  const [recentCommands, setRecentCommands] = useState([]);
  const [actionLoading, setActionLoading] = useState({});
  const [lastRefresh, setLastRefresh] = useState(null);
  
  // Action confirmation modal
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedBarrier, setSelectedBarrier] = useState(null);
  const [selectedAction, setSelectedAction] = useState('');
  const [actionReason, setActionReason] = useState('');

  const fetchBarrierStatus = useCallback(async () => {
    try {
      // Get cached status from VPS (populated by local bridge)
      const response = await fetch(`${API_URL}/api/barrier-bridge/status?bridge_id=office-bridge`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        setBarriers(data.barriers || []);
        setStatusInfo({
          updated_at: data.updated_at,
          is_stale: data.is_stale,
          success: data.success
        });
        setLastRefresh(new Date());
      }
    } catch (error) {
      console.error('Error fetching barrier status:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBridgeHealth = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/barrier-bridge/health?bridge_id=office-bridge`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        setBridgeHealth(data);
      }
    } catch (error) {
      console.error('Error fetching bridge health:', error);
    }
  }, []);

  const fetchRecentCommands = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/barrier-bridge/commands/recent?limit=10`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        setRecentCommands(data.commands || []);
      }
    } catch (error) {
      console.error('Error fetching commands:', error);
    }
  }, []);

  useEffect(() => {
    fetchBarrierStatus();
    fetchBridgeHealth();
    fetchRecentCommands();
  }, [fetchBarrierStatus, fetchBridgeHealth, fetchRecentCommands]);

  // Auto-refresh every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchBarrierStatus();
      fetchBridgeHealth();
      fetchRecentCommands();
    }, 3000);
    return () => clearInterval(interval);
  }, [fetchBarrierStatus, fetchBridgeHealth, fetchRecentCommands]);

  const queueCommand = async (barrierId, action) => {
    setActionLoading(prev => ({ ...prev, [`${barrierId}-${action}`]: true }));
    
    try {
      // Queue command via VPS (bridge will pick it up)
      const response = await fetch(`${API_URL}/api/barrier-bridge/command/queue`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          barrier_id: barrierId,
          action: action,
          reason: actionReason || `Manual ${action} from admin`,
          source: 'admin_dashboard'
        }),
      });
      
      const result = await response.json();
      
      if (response.ok) {
        toast.success(`${action.toUpperCase()} command queued for ${barrierId}`);
        fetchRecentCommands();
      } else {
        toast.error(`Failed to queue command: ${result.detail || 'Unknown error'}`);
      }
    } catch (error) {
      toast.error(`Failed to queue command: ${error.message}`);
    } finally {
      setActionLoading(prev => ({ ...prev, [`${barrierId}-${action}`]: false }));
      setShowActionModal(false);
      setActionReason('');
    }
  };

  const openActionModal = (barrier, action) => {
    setSelectedBarrier(barrier);
    setSelectedAction(action);
    setShowActionModal(true);
  };

  const DirectionBadge = ({ direction }) => {
    const config = directionConfig[direction] || directionConfig.entry;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const CoilIndicator = ({ name, active }) => (
    <div className={`flex items-center gap-1 text-xs ${active ? 'text-green-600' : 'text-gray-400'}`}>
      <div className={`w-2 h-2 rounded-full ${active ? 'bg-green-500' : 'bg-gray-300'}`} />
      {name}
    </div>
  );

  const CommandStatusBadge = ({ status }) => {
    const config = commandStatusConfig[status] || commandStatusConfig.queued;
    return (
      <Badge className={config.color}>
        {config.label}
      </Badge>
    );
  };

  return (
    <div className="p-6 space-y-6" data-testid="admin-barrier-control-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Live Barrier Control</h1>
          <p className="text-gray-500">Physical barrier status via local bridge</p>
        </div>
        <div className="flex items-center gap-4">
          {/* Bridge Status */}
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
            bridgeHealth.is_online ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
            {bridgeHealth.is_online ? (
              <Wifi className="h-4 w-4" />
            ) : (
              <WifiOff className="h-4 w-4" />
            )}
            <span className="text-sm font-medium">
              Bridge {bridgeHealth.is_online ? 'Online' : 'Offline'}
            </span>
          </div>
          
          <Button
            variant="outline"
            onClick={() => { fetchBarrierStatus(); fetchBridgeHealth(); fetchRecentCommands(); }}
            disabled={loading}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Bridge & Status Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-full ${bridgeHealth.is_online ? 'bg-green-100' : 'bg-red-100'}`}>
                  <Server className={`h-6 w-6 ${bridgeHealth.is_online ? 'text-green-600' : 'text-red-600'}`} />
                </div>
                <div>
                  <p className="font-semibold">Local Bridge</p>
                  <p className="text-sm text-gray-500">{bridgeHealth.bridge_id || 'office-bridge'}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">Last Heartbeat</p>
                <p className="font-medium text-sm">
                  {bridgeHealth.last_heartbeat ? formatDateTime(bridgeHealth.last_heartbeat) : 'Never'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-full ${!statusInfo.is_stale ? 'bg-green-100' : 'bg-yellow-100'}`}>
                  <Activity className={`h-6 w-6 ${!statusInfo.is_stale ? 'text-green-600' : 'text-yellow-600'}`} />
                </div>
                <div>
                  <p className="font-semibold">Barrier Status</p>
                  <p className="text-sm text-gray-500">
                    {statusInfo.is_stale ? 'Stale (>30s old)' : 'Live'}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">Last Update</p>
                <p className="font-medium text-sm">
                  {statusInfo.updated_at ? formatDateTime(statusInfo.updated_at) : 'Never'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {!bridgeHealth.is_online && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-yellow-600" />
            <div>
              <p className="font-medium text-yellow-800">Local Bridge Offline</p>
              <p className="text-sm text-yellow-700">
                The local barrier bridge is not connected. Commands will be queued but not executed until the bridge comes online.
                Ensure the bridge is running on the office LAN.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Barriers Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading && barriers.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
            </CardContent>
          </Card>
        ) : barriers.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="text-center py-12">
              <Shield className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500">No barriers detected</p>
              <p className="text-sm text-gray-400 mt-1">
                Waiting for local bridge to report barrier status
              </p>
            </CardContent>
          </Card>
        ) : (
          barriers.map((barrier) => (
            <Card 
              key={barrier.id} 
              className="overflow-hidden"
              data-testid={`barrier-card-${barrier.id}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">{barrier.name}</CardTitle>
                    <CardDescription className="font-mono">{barrier.id}</CardDescription>
                  </div>
                  <DirectionBadge direction={barrier.direction} />
                </div>
              </CardHeader>
              
              <CardContent className="space-y-4">
                {/* Status Row */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Site</p>
                    <p className="font-medium">{barrier.site}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Latency</p>
                    <p className="font-medium">{barrier.latencyMs}ms</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Last Action</p>
                    <Badge variant="outline" className="capitalize">
                      {barrier.lastAction || 'None'}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-gray-500">Locked</p>
                    <Badge variant={barrier.locked ? 'destructive' : 'secondary'}>
                      {barrier.locked ? 'Yes' : 'No'}
                    </Badge>
                  </div>
                </div>

                {/* Coils Status */}
                <div className="border-t pt-3">
                  <p className="text-xs text-gray-500 mb-2">Coil Status</p>
                  <div className="flex gap-4">
                    <CoilIndicator name="Lift" active={barrier.coils?.lift} />
                    <CoilIndicator name="Stop" active={barrier.coils?.stop} />
                    <CoilIndicator name="Close" active={barrier.coils?.close} />
                  </div>
                </div>

                {/* Latched Action */}
                {barrier.latchedAction && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2">
                    <p className="text-xs text-yellow-800">
                      <Zap className="h-3 w-3 inline mr-1" />
                      Latched: {barrier.latchedAction}
                    </p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="grid grid-cols-2 gap-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
                    onClick={() => openActionModal(barrier, 'lift')}
                    disabled={actionLoading[`${barrier.id}-lift`]}
                    data-testid={`lift-btn-${barrier.id}`}
                  >
                    {actionLoading[`${barrier.id}-lift`] ? (
                      <RefreshCw className="h-4 w-4 animate-spin mr-1" />
                    ) : (
                      <ChevronUp className="h-4 w-4 mr-1" />
                    )}
                    Lift
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    className="bg-gray-50 hover:bg-gray-100 text-gray-700 border-gray-200"
                    onClick={() => openActionModal(barrier, 'close')}
                    disabled={actionLoading[`${barrier.id}-close`]}
                    data-testid={`close-btn-${barrier.id}`}
                  >
                    {actionLoading[`${barrier.id}-close`] ? (
                      <RefreshCw className="h-4 w-4 animate-spin mr-1" />
                    ) : (
                      <ChevronDown className="h-4 w-4 mr-1" />
                    )}
                    Close
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    className={barrier.locked ? 
                      "bg-green-50 hover:bg-green-100 text-green-700 border-green-200" : 
                      "bg-red-50 hover:bg-red-100 text-red-700 border-red-200"
                    }
                    onClick={() => openActionModal(barrier, barrier.locked ? 'unlock' : 'lock')}
                    disabled={actionLoading[`${barrier.id}-lock`] || actionLoading[`${barrier.id}-unlock`]}
                    data-testid={`lock-btn-${barrier.id}`}
                  >
                    {barrier.locked ? (
                      <>
                        <Unlock className="h-4 w-4 mr-1" />
                        Unlock
                      </>
                    ) : (
                      <>
                        <Lock className="h-4 w-4 mr-1" />
                        Lock
                      </>
                    )}
                  </Button>
                  
                  {barrier.latchedAction && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="bg-yellow-50 hover:bg-yellow-100 text-yellow-700 border-yellow-200"
                      onClick={() => openActionModal(barrier, 'unlatch')}
                      disabled={actionLoading[`${barrier.id}-unlatch`]}
                      data-testid={`unlatch-btn-${barrier.id}`}
                    >
                      <XCircle className="h-4 w-4 mr-1" />
                      Unlatch
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Recent Commands */}
      {recentCommands.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Recent Commands
            </CardTitle>
            <CardDescription>Commands queued and executed via local bridge</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Time</TableHead>
                  <TableHead>Barrier</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Source</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentCommands.slice(0, 5).map((cmd) => (
                  <TableRow key={cmd.command_id}>
                    <TableCell className="text-sm">
                      {formatDateTime(cmd.queued_at)}
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {cmd.barrier_id}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {cmd.action}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <CommandStatusBadge status={cmd.status} />
                    </TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {cmd.source}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Stats Summary */}
      {barriers.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Barriers</p>
                  <p className="text-2xl font-bold">{barriers.length}</p>
                </div>
                <Shield className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Entry</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {barriers.filter(b => b.direction === 'entry').length}
                  </p>
                </div>
                <ChevronDown className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Exit</p>
                  <p className="text-2xl font-bold text-green-600">
                    {barriers.filter(b => b.direction === 'exit').length}
                  </p>
                </div>
                <ChevronUp className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Locked</p>
                  <p className="text-2xl font-bold text-red-600">
                    {barriers.filter(b => b.locked).length}
                  </p>
                </div>
                <Lock className="h-8 w-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Action Confirmation Modal */}
      <Dialog open={showActionModal} onOpenChange={setShowActionModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedAction === 'lift' && <ChevronUp className="h-5 w-5 text-green-600" />}
              {selectedAction === 'close' && <ChevronDown className="h-5 w-5 text-gray-600" />}
              {selectedAction === 'lock' && <Lock className="h-5 w-5 text-red-600" />}
              {selectedAction === 'unlock' && <Unlock className="h-5 w-5 text-green-600" />}
              {selectedAction === 'unlatch' && <XCircle className="h-5 w-5 text-yellow-600" />}
              Queue {selectedAction?.toUpperCase()} Command
            </DialogTitle>
            <DialogDescription>
              Send {selectedAction} command to {selectedBarrier?.name} ({selectedBarrier?.id})
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800 flex items-center gap-2">
                <Server className="h-4 w-4" />
                <span>Command will be queued and executed by the local bridge</span>
              </p>
            </div>
            
            {!bridgeHealth.is_online && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  <span>Bridge offline - command will be queued until bridge reconnects</span>
                </p>
              </div>
            )}
            
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                Reason (Optional)
              </label>
              <Input
                value={actionReason}
                onChange={(e) => setActionReason(e.target.value)}
                placeholder="Enter reason for action..."
                data-testid="action-reason-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActionModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => queueCommand(selectedBarrier?.id, selectedAction)}
              disabled={actionLoading[`${selectedBarrier?.id}-${selectedAction}`]}
              className={
                selectedAction === 'lift' ? 'bg-green-600 hover:bg-green-700' :
                selectedAction === 'lock' ? 'bg-red-600 hover:bg-red-700' :
                ''
              }
              data-testid="confirm-action-btn"
            >
              {actionLoading[`${selectedBarrier?.id}-${selectedAction}`] ? (
                <RefreshCw className="h-4 w-4 animate-spin mr-2" />
              ) : (
                selectedAction === 'lift' ? <ChevronUp className="h-4 w-4 mr-2" /> :
                selectedAction === 'close' ? <ChevronDown className="h-4 w-4 mr-2" /> :
                selectedAction === 'lock' ? <Lock className="h-4 w-4 mr-2" /> :
                selectedAction === 'unlock' ? <Unlock className="h-4 w-4 mr-2" /> :
                <XCircle className="h-4 w-4 mr-2" />
              )}
              Queue {selectedAction?.toUpperCase()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
